import React, { useEffect, useState } from 'react'

import { useApiKeyActions } from 'redux/actions/ApiKeys'
import { useExportTableActions } from 'redux/actions/ExportTables'

import { useExportTableApiKeys } from 'redux/hooks/ApiKeys'
import { useExportTablesLoading } from 'redux/hooks/ExportTables'

import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import InputAdornment from 'components_new/atoms/InputAdornment'
import List from 'components_new/atoms/List'
import ListItem from 'components_new/atoms/List/ListItem'
import ListItemText from 'components_new/atoms/List/ListItemText'
import Switch from 'components_new/atoms/Switch'
import TextField from 'components_new/atoms/TextField'
import Tooltip from 'components_new/atoms/Tooltip'

import FormSection from 'components_new/molecules/FormSection'
import SelectSearch from 'components_new/molecules/SelectSearch'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import SidebarDialog from 'components_new/molecules/SidebarDialog'

import CreateApiKeyDialog from 'components_new/organisms/dialogs/CreateApiKeyDialog'
import { useSnackbarActions } from 'redux/actions/Snackbar'

/* Hooks */
import { useTranslation } from 'language/translator'

/* Types */
import { ApiKeyType } from 'types/GlobalApiKey'
import { TExportTableWithWidget } from 'types/GlobalExportTable'
import { SnackbarMessage } from 'types/GlobalSnackbar'

/* Local files */
import { SlugDialog } from './SlugDialog'
import { getExportTableEndpoint } from './utils'

interface ExportTableShareDialogProps {
  exportTable: TExportTableWithWidget
  onClose: () => void
  open: boolean
}

export const ExportTableShareDialog = (props: ExportTableShareDialogProps) => {
  const { exportTable, onClose, open } = props

  const [slugDialogOpen, setSlugDialogOpen] = useState<{
    open: boolean
    variant: 'activate' | 'edit'
  }>({
    open: false,
    variant: 'activate'
  })

  const [createApiKeyOpen, setCreateApiKeyOpen] = useState(false)

  const { t } = useTranslation()

  const { tryAssignApiKeysToExportTable, tryUpdateExportTable } =
    useExportTableActions()
  const { tryGetAllApiKeys } = useApiKeyActions()

  const apiKeys = useExportTableApiKeys()
  const { assigningApiKeys } = useExportTablesLoading()
  const { toggleSnackbarOpen } = useSnackbarActions()

  useEffect(() => {
    if (!apiKeys.fetched) {
      tryGetAllApiKeys()
    }
  }, [])

  return (
    <SidebarDialog
      actions={
        <Button loading={assigningApiKeys} onClick={onClose} variant={'text'}>
          {t('global', 'close')}
        </Button>
      }
      title={t('global', 'share_name', false, { name: exportTable.title })}
      open={open}
      onClose={onClose}
    >
      <FormSection
        helperText={t('export_tables', 'api_access_helper')}
        title={t('export_tables', 'api_access_title')}
      >
        <SettingsGroup>
          <SettingsItem
            actions={
              <Switch
                color="success"
                checked={!!exportTable.slug}
                onChange={() => {
                  if (exportTable.slug === null) {
                    setSlugDialogOpen({ open: true, variant: 'activate' })
                  } else {
                    tryUpdateExportTable(exportTable.id, {
                      data: { slug: null }
                    })
                  }
                }}
                size="medium"
              />
            }
            body={t('export_tables', 'available_via_api_body')}
            icon={<Icon name={'PublicOutlined'} />}
            title={t('export_tables', 'available_via_api')}
          />
        </SettingsGroup>
      </FormSection>

      <Collapse in={!!exportTable.slug}>
        <FormSection
          helperText={t('export_tables', 'endpoint_helper')}
          title={t('export_tables', 'endpoint_title')}
        >
          <TextField
            value={getExportTableEndpoint(exportTable.slug)}
            fullWidth
            helperText={t('export_tables', 'endpoint_auth_helper')}
            InputProps={{
              endAdornment: (
                <>
                  <InputAdornment position="end">
                    <Tooltip title={t('export_tables', 'copy_endpoint')}>
                      <IconButton
                        color="primary"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            getExportTableEndpoint(exportTable.slug)
                          )

                          toggleSnackbarOpen(SnackbarMessage.CopiedToClipboard)
                        }}
                      >
                        <Icon name="Copy" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                  <InputAdornment position="end">
                    <Tooltip title={t('export_tables', 'edit_slug')}>
                      <IconButton
                        color="primary"
                        onClick={() =>
                          setSlugDialogOpen({ open: true, variant: 'edit' })
                        }
                      >
                        <Icon name="Edit" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                </>
              ),
              readOnly: true
            }}
            sx={{ mt: 1 }}
          />
        </FormSection>

        <FormSection
          helperText={t('export_tables', 'authorized_api_keys_helper')}
          title={t('export_tables', 'authorized_api_keys_title')}
        >
          <SelectSearch
            closeOnSelect={true}
            color="primary"
            disableClearable={true}
            label={t('export_tables', 'add_api_key')}
            onChange={(option) => {
              if (option.id === '__create_new__') {
                setCreateApiKeyOpen(true)
              } else {
                tryAssignApiKeysToExportTable(exportTable.id, [
                  ...exportTable.api_keys,
                  option.id as string
                ])
              }
            }}
            multiple={false}
            options={[
              ...apiKeys.keys
                .filter((key) => !exportTable.api_keys.includes(key.id))
                .map((key) => ({
                  id: key.id,
                  title: key.name
                })),
              {
                id: '__create_new__',
                title: t('export_tables', 'create_api_key'),
                icon: 'Add'
              }
            ]}
            selected={null}
            size={'medium'}
            sort={false}
            sx={{ mb: 2 }}
          />

          {exportTable.api_keys.length > 0 && (
            <List dense sx={{ pt: 0 }}>
              {apiKeys.keys
                .filter((key) => exportTable.api_keys.includes(key.id))
                .map((key) => (
                  <ListItem
                    key={key.id}
                    secondaryAction={
                      <Tooltip title={t('export_tables', 'remove_api_key')}>
                        <IconButton
                          onClick={() => {
                            tryAssignApiKeysToExportTable(
                              exportTable.id,
                              exportTable.api_keys.filter(
                                (currentKey) => currentKey !== key.id
                              )
                            )
                          }}
                          size="small"
                        >
                          <Icon
                            fontSize="small"
                            name="RemoveCircleOutlineOutlined"
                          />
                        </IconButton>
                      </Tooltip>
                    }
                    sx={{
                      px: 0,
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemText
                      primary={key.name}
                      secondary={'••••••••••••••••••••••••••••••••'}
                      primaryTypographyProps={{
                        fontWeight: 500,
                        ml: 1
                      }}
                      secondaryTypographyProps={{
                        color: 'text.secondary',
                        ml: 1
                      }}
                    />
                  </ListItem>
                ))}
            </List>
          )}
        </FormSection>
      </Collapse>

      <SlugDialog
        exportTable={exportTable}
        onClose={() => setSlugDialogOpen({ open: false, variant: 'activate' })}
        open={slugDialogOpen.open}
        variant={slugDialogOpen.variant}
      />

      <CreateApiKeyDialog
        fixedType={ApiKeyType.EXPORT_TABLE}
        onClose={() => setCreateApiKeyOpen(false)}
        open={createApiKeyOpen}
      />
    </SidebarDialog>
  )
}
